import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Input, Modal, Select } from '@/shared/ui';

import { DocumentType, EncounterSchema } from '@/entities/templates';

import { templateListMessages } from '@/features/templateList';

import {
  DOCUMENT_TYPE_DESCRIPTIONS,
  DOCUMENT_TYPE_OPTIONS,
  LANGUAGE_MESSAGES,
  STATUS_OPTIONS,
  type TemplateDetailsValues,
  templateEditorMessages,
} from '../../config';
import { EditorSelect } from '../EditorSelect';
import { TemplateSpecialtiesSelect } from '../TemplateSpecialtiesSelect';

import styles from './TemplateDetailsModal.module.scss';

type TemplateDetailsModalProps = {
  isOpen: boolean;
  values: TemplateDetailsValues;
  specialtyOptions: EncounterSchema[];
  languageLocked?: boolean;
  readOnly?: boolean;
  onSave: (values: TemplateDetailsValues) => void;
  onClose: () => void;
};

export const TemplateDetailsModal: FC<TemplateDetailsModalProps> = (props) => {
  const {
    isOpen,
    values,
    specialtyOptions,
    languageLocked,
    readOnly,
    onSave,
    onClose,
  } = props;

  const { formatMessage } = useIntl();

  const [draft, setDraft] = useState<TemplateDetailsValues>(values);

  // The draft is discarded on Cancel, so it is re-seeded every time the
  // modal opens rather than kept in sync with the form on every keystroke.
  useEffect(() => {
    if (isOpen) {
      setDraft(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const languageOptions = Object.entries(LANGUAGE_MESSAGES).map(
    ([value, message]) => ({ value, label: formatMessage(message) }),
  );
  const statusOptions = STATUS_OPTIONS.map((option) => ({
    value: option.value,
    label: formatMessage(option.labelMessage),
  }));
  const documentTypeOptions = DOCUMENT_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: formatMessage(option.labelMessage),
    description: formatMessage(DOCUMENT_TYPE_DESCRIPTIONS[option.value]),
  }));

  const isSaveDisabled = readOnly || !draft.name.trim();

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraft((prev) => ({ ...prev, name: event.target.value }));
  };

  const handleSave = () => {
    onSave({ ...draft, name: draft.name.trim() });
  };

  return (
    <Modal
      isOpen={isOpen}
      borderless
      dataComponent="template-details-modal"
      title={formatMessage(templateEditorMessages.metaFormTitle)}
      okButtonText={formatMessage(templateEditorMessages.save)}
      cancelButtonText={formatMessage(templateEditorMessages.cancel)}
      okButtonProps={{ disabled: isSaveDisabled }}
      onOk={handleSave}
      onCancel={onClose}
    >
      <div className={styles.body}>
        <Input
          name="templateName"
          label={formatMessage(templateEditorMessages.templateName)}
          required
          value={draft.name}
          disabled={readOnly}
          onChange={handleNameChange}
        />

        <TemplateSpecialtiesSelect
          className={styles.field}
          options={specialtyOptions}
          value={draft.schemaIds}
          disabled={readOnly}
          onChange={(schemaIds) => setDraft((prev) => ({ ...prev, schemaIds }))}
        />

        <div className={styles.row}>
          <Select
            testID="template-language-select"
            label={formatMessage(templateEditorMessages.language)}
            options={languageOptions}
            value={draft.language}
            isDisabled={readOnly || languageLocked}
            onChange={(language) =>
              setDraft((prev) => ({ ...prev, language: String(language) }))
            }
          />
          <EditorSelect<DocumentType>
            dataComponent="template-type-select"
            label={formatMessage(templateListMessages.type)}
            options={documentTypeOptions}
            value={draft.documentType}
            disabled={readOnly}
            onChange={(documentType) =>
              setDraft((prev) => ({ ...prev, documentType }))
            }
          />
        </div>

        <Select
          testID="template-status-select"
          label={formatMessage(templateEditorMessages.status)}
          options={statusOptions}
          value={draft.status}
          isDisabled={readOnly}
          onChange={(status) => {
            const statusOption = STATUS_OPTIONS.find(
              (option) => option.value === status,
            );
            if (statusOption) {
              setDraft((prev) => ({ ...prev, status: statusOption.value }));
            }
          }}
        />
      </div>
    </Modal>
  );
};
